/**
 * Sundial Release Calendar — Client-side Engine
 *
 * Responsibilities:
 *   1. Load compiled release data from the JSON asset
 *   2. Parse active filters from the URL
 *   3. Render the calendar into #rc-root
 *   4. Handle filter interactions and update the URL without page reloads
 *   5. Handle search with debouncing
 *
 * The data URL is read from the [data-releases-url] attribute on #rc-root.
 * Schema constants are inlined here to avoid a second asset request.
 *
 * No framework dependencies. Runs after DOMContentLoaded.
 */

(function () {
  'use strict';

  // -------------------------------------------------------------------------
  // Constants (mirrored from scripts/release-calendar/lib/release-schema.mjs)
  // -------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Premium SVG shoe silhouettes (viewBox 0 0 120 60, toe points RIGHT)
  // ---------------------------------------------------------------------------

  const SVG_SHOES = {
    road_running: '<svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 52C10 52 8 46 12 44L20 42C28 36 44 22 62 20C78 20 96 26 106 36C112 40 114 46 110 52H16Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M16 44C14 38 13 30 16 20C18 14 24 10 28 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M20 52L20 56M34 52L34 56M48 52L48 56M62 52L62 56M76 52L76 56M90 52L90 56M104 52L104 56" stroke="currentColor" stroke-width="1" stroke-linecap="round" opacity="0"/><path d="M14 46L108 46" stroke="currentColor" stroke-width="1.5" opacity="0.5"/><path d="M42 20C52 16 68 16 80 20" stroke="currentColor" stroke-width="1.5" stroke-dasharray="3 2.5" stroke-linecap="round"/></svg>',

    trail_running: '<svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 50C10 50 7 44 11 42L20 40C28 34 44 20 62 18C79 18 98 25 108 36C114 40 116 46 112 50H16Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M16 42C14 36 13 28 16 18C18 12 24 8 28 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M14 50L18 56M26 50L30 56M38 50L42 56M52 50L56 56M66 50L70 56M80 50L84 56M94 50L98 56M106 50L110 56" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M40 18C52 14 68 14 80 18" stroke="currentColor" stroke-width="1.5" stroke-dasharray="3 2.5" stroke-linecap="round"/></svg>',

    work_safety: '<svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6H42C44 6 46 8 46 10V30L68 28C88 26 106 32 112 40C116 44 114 52 108 54H14C10 54 8 50 10 46L12 40V6" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/><path d="M14 54L112 54" stroke="currentColor" stroke-width="2"/><line x1="18" y1="16" x2="44" y2="16" stroke="currentColor" stroke-width="1.5"/><line x1="18" y1="22" x2="45" y2="22" stroke="currentColor" stroke-width="1.5"/><line x1="18" y1="28" x2="44" y2="28" stroke="currentColor" stroke-width="1.5"/></svg>',

    work_safety_safety: '<svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6H42C44 6 46 8 46 10V30L68 28C88 26 106 32 112 40C116 44 114 52 108 54H14C10 54 8 50 10 46L12 40V6" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/><path d="M98 36C108 36 116 42 116 48" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round"/><path d="M14 54L112 54" stroke="currentColor" stroke-width="2"/><line x1="18" y1="16" x2="44" y2="16" stroke="currentColor" stroke-width="1.5"/><line x1="18" y1="22" x2="45" y2="22" stroke="currentColor" stroke-width="1.5"/></svg>',

    walking_everyday: '<svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 50C14 50 10 44 14 42L22 40C30 36 46 24 64 22C80 22 98 28 106 38C110 42 112 48 108 50H20Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M20 42C18 36 17 30 20 22C22 16 28 14 32 16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M10 48C8 46 9 42 12 42" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M44 22C56 18 72 18 82 22" stroke="currentColor" stroke-width="1.5" stroke-dasharray="3 2.5" stroke-linecap="round"/><path d="M16 46L106 46" stroke="currentColor" stroke-width="1.5" opacity="0.5"/></svg>',

    sandals_slides: '<svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="12" y="44" width="98" height="12" rx="6" stroke="currentColor" stroke-width="2"/><path d="M30 44C30 36 36 24 50 20C60 16 72 18 78 24C84 30 84 38 84 44" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/><path d="M54 44L54 32C56 28 60 24 62 22" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/></svg>',

    track_racing: '<svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 52C8 52 6 44 10 42L18 40C24 34 40 18 60 16C76 14 96 22 108 34C114 38 116 46 112 52H14Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M14 42C12 36 11 28 14 16C16 10 22 6 26 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M38 16C50 12 66 10 80 14" stroke="currentColor" stroke-width="1.5" stroke-dasharray="3 2.5" stroke-linecap="round"/><path d="M10 44L112 44" stroke="currentColor" stroke-width="1" opacity="0.4"/><path d="M92 38C96 30 98 24 96 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" opacity="0.6"/></svg>',

    hiking: '<svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 8H44C46 8 48 10 48 12V32L70 30C90 28 108 34 114 42C118 46 116 54 110 56H14C10 56 8 52 10 48L12 42V8" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/><path d="M14 56L110 56" stroke="currentColor" stroke-width="2"/><line x1="18" y1="18" x2="46" y2="18" stroke="currentColor" stroke-width="1.5"/><line x1="18" y1="24" x2="47" y2="24" stroke="currentColor" stroke-width="1.5"/><path d="M14 56L18 60M28 56L32 60M44 56L48 60M60 56L64 60M76 56L80 60M92 56L96 60M106 56L110 60" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',

    default: '<svg viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 52C10 52 8 46 12 44L20 42C28 36 44 22 62 20C78 20 96 26 106 36C112 40 114 46 110 52H16Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" opacity="0.4"/><path d="M16 44C14 38 13 30 16 20C18 14 24 10 28 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" opacity="0.4"/><text x="62" y="42" text-anchor="middle" font-size="18" font-family="system-ui,sans-serif" fill="currentColor" font-weight="700" opacity="0.4">?</text></svg>',
  };

  // Category → placeholder background tint (very subtle)
  const PLACEHOLDER_TINTS = {
    road_running:     '#eef2ff',
    trail_running:    '#ecfdf5',
    work_safety:      '#fffbeb',
    walking_everyday: '#faf5ff',
    sandals_slides:   '#fff1f2',
    track_racing:     '#eff6ff',
    hiking:           '#f7fee7',
    default:          '#f8fafc',
  };

  // -------------------------------------------------------------------------
  // JSON-LD injection — called lazily after data loads
  // -------------------------------------------------------------------------

  /**
   * Inject compiled JSON-LD schemas into the document <head>.
   * Called once after the LD file loads; subsequent calls replace the old tags.
   * Injects FAQPage, ItemList, Dataset, and CollectionPage schemas.
   *
   * @param {object} ldData — parsed release-calendar-ld.json
   */
  function injectJsonLD(ldData) {
    if (!ldData || !ldData.schemas) return;

    // Remove any existing dynamic LD tags we injected previously
    document.querySelectorAll('script[data-rc-ld]').forEach(function(el) {
      el.remove();
    });

    var schemas = ldData.schemas;
    var order = ['faq_page', 'item_list', 'dataset', 'collection_page', 'product_graph'];

    for (var i = 0; i < order.length; i++) {
      var key = order[i];
      var schema = schemas[key];
      if (!schema) continue;

      var script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-rc-ld', key);
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }
  }

  const RELEASE_STATUS_LABELS = {
    released:             'Released',
    upcoming_confirmed:   'Coming Soon',
    upcoming_announced:   'Announced',
    upcoming_estimated:   'Expected',
    rumored:              'Rumored',
    delayed:              'Delayed',
    cancelled:            'Cancelled',
    discontinued:         'Discontinued',
  };

  const CATEGORY_LABELS = {
    road_running:     'Road Running',
    trail_running:    'Trail Running',
    track_racing:     'Track & Racing',
    work_safety:      'Work & Safety',
    hiking:           'Hiking',
    walking_everyday: 'Walking & Everyday',
    sandals_slides:   'Sandals & Slides',
  };

  const BUY_LABELS = {
    buy_now:             'Buy Now',
    wait:                'Wait',
    buy_predecessor:     'Buy Previous',
    wait_or_predecessor: 'Wait or Buy Previous',
    either:              'Either Option',
    not_yet_determined:  '',
  };

  const VISIBLE_STATUSES = new Set([
    'released', 'upcoming_confirmed', 'upcoming_announced',
    'upcoming_estimated', 'rumored', 'delayed',
  ]);

  const FILTER_PARAMS = { brand: 'brand', category: 'category', activity: 'activity', gender: 'gender', status: 'status', year: 'year', month: 'month', search: 'q', sort: 'sort' };

  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------

  let allReleases = [];
  let meta = null;
  let alertsConfig = null;
  let faqGroups = { categories: [], site: [] };
  let activeAlertContext = null;
  let alertModalInitialized = false;
  const state = {
    brand: null, category: null, activity: null,
    gender: null, status: null, year: null, month: null,
    search: '', sort: 'date_asc',
  };

  // -------------------------------------------------------------------------
  // Entry point
  // -------------------------------------------------------------------------

  document.addEventListener('DOMContentLoaded', function () {
    const root = document.getElementById('rc-root');
    if (!root) return;

    const url = root.dataset.releasesUrl;
    if (!url) {
      root.innerHTML = '<p class="rc-empty">Release data URL not configured.</p>';
      return;
    }

    readUrlState();
    renderLoading(root);

    fetch(url)
      .then(r => r.json())
      .then(data => {
        allReleases = (data.releases || []).filter(r => VISIBLE_STATUSES.has(r.release_status));
        meta = data._meta || null;
        alertsConfig = data.alerts || null;
        faqGroups = data.faqs || { categories: [], site: [] };
        render(root);
        populateFilters(root);
        attachListeners(root);
        populateAlertUi();
        attachAlertModal();
        scrollToHashedRelease();

        // Lazily load JSON-LD + inject into <head> — does not block the UI
        var ldUrl = root.dataset.ldUrl;
        if (ldUrl) {
          fetch(ldUrl)
            .then(function(r) { return r.json(); })
            .then(function(ldData) { injectJsonLD(ldData); })
            .catch(function() { /* non-critical — silently skip if LD file not found */ });
        }
      })
      .catch(err => {
        console.error('[release-calendar]', err);
        root.innerHTML = '<p class="rc-empty">Unable to load release data. Please try again later.</p>';
      });
  });

  // -------------------------------------------------------------------------
  // URL state
  // -------------------------------------------------------------------------

  function readUrlState() {
    const params = new URLSearchParams(window.location.search);
    for (const [key, param] of Object.entries(FILTER_PARAMS)) {
      const val = params.get(param);
      if (val) state[key] = val;
    }
  }

  function pushUrlState() {
    const params = new URLSearchParams();
    for (const [key, param] of Object.entries(FILTER_PARAMS)) {
      const val = state[key];
      if (val && val !== '' && val !== 'date_asc') params.set(param, val);
    }
    const search = params.toString();
    const newUrl = window.location.pathname + (search ? '?' + search : '') + window.location.hash;
    window.history.pushState({ filters: { ...state } }, '', newUrl);
  }

  window.addEventListener('popstate', function (e) {
    if (e.state && e.state.filters) Object.assign(state, e.state.filters);
    else { for (const k of Object.keys(state)) state[k] = k === 'search' ? '' : k === 'sort' ? 'date_asc' : null; }
    const root = document.getElementById('rc-root');
    if (root) { render(root); syncFilterUI(root); }
  });

  // -------------------------------------------------------------------------
  // Filtering
  // -------------------------------------------------------------------------

  function applyFilters(releases) {
    return releases.filter(r => {
      if (state.brand && r.brand_slug !== state.brand) return false;
      if (state.category && r.category !== state.category) return false;
      if (state.activity && r.activity !== state.activity) return false;
      if (state.gender && state.gender !== 'both' && r.gender !== state.gender && r.gender !== 'both' && r.gender !== 'unisex') return false;
      if (state.status) {
        if (state.status === 'released'      && !r._is_released)  return false;
        if (state.status === 'upcoming'      && !r._is_upcoming)  return false;
        if (state.status === 'rumored'       && r.release_status !== 'rumored') return false;
        if (state.status === 'recent'        && !r._is_recently_released) return false;
        if (state.status === 'soon'          && !r._is_coming_soon) return false;
        if (state.status === 'worth_waiting' && r._verdict_label !== 'Worth Waiting For') return false;
      }
      if (state.year  && r._year_key  !== state.year)  return false;
      if (state.month && r._month_key !== state.month) return false;
      if (state.search && state.search.trim()) {
        const q = state.search.trim().toLowerCase();
        const haystack = [r.brand, r.model, r.version_label, r.category].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }

  function sortReleases(releases) {
    const copy = [...releases];
    if (state.sort === 'brand_asc') {
      return copy.sort((a, b) => (a.brand_slug || '').localeCompare(b.brand_slug || '') || (a.model_slug || '').localeCompare(b.model_slug || ''));
    }
    if (state.sort === 'date_desc') {
      return copy.sort((a, b) => (a._sort_key || '').localeCompare(b._sort_key || ''));
    }
    // Default: date_asc — upcoming soonest first, then released newest first
    return copy.sort((a, b) => {
      const aUp = a._is_upcoming ? 0 : 1;
      const bUp = b._is_upcoming ? 0 : 1;
      if (aUp !== bUp) return aUp - bUp;
      return a._is_upcoming
        ? (a._sort_key || '').localeCompare(b._sort_key || '')
        : (b._sort_key || '').localeCompare(a._sort_key || '');
    });
  }

  function groupByMonth(releases) {
    const map = new Map();
    for (const r of releases) {
      const key = r._month_key || '_unknown';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return map;
  }

  // -------------------------------------------------------------------------
  // Rendering
  // -------------------------------------------------------------------------

  function renderLoading(root) {
    var cards = '';
    var lineWidths = [['38%','70%','52%'], ['42%','68%','50%','80%'], ['35%','72%','55%'], ['40%','65%','58%','75%']];
    for (var i = 0; i < 4; i++) {
      var widths = lineWidths[i];
      var lines = widths.map(function(w) {
        return '<div class="rc-skeleton rc-skeleton--line" style="width:' + w + '"></div>';
      }).join('');
      cards += '<article class="rc-card" aria-hidden="true"><div class="rc-card__visual"><div class="rc-skeleton rc-skeleton--thumb"></div></div><div class="rc-card__body">' + lines + '</div></article>';
    }
    root.innerHTML = '<div class="rc-calendar"><div class="rc-month__releases" style="display:flex;flex-direction:column;gap:0.75rem">' + cards + '</div></div>';
  }

  function render(root) {
    const filtered = sortReleases(applyFilters(allReleases));
    const groups = groupByMonth(filtered);
    const isDefaultView = !state.brand && !state.category && !state.activity
      && !state.gender && !state.status && !state.year && !state.month
      && !state.search;

    // Replace content area only (not controls)
    const resultsEl = root.querySelector('.rc-calendar__results');
    if (!resultsEl) {
      // First render: build the full structure
      root.innerHTML = `
        <div class="rc-calendar">
          <div class="rc-calendar__controls" id="rc-controls"></div>
          <div id="rc-chips" class="rc-chips" role="group" aria-label="Quick filters"></div>
          <div id="rc-featured"></div>
          <div id="rc-results-bar"></div>
          <div class="rc-calendar__results" id="rc-results"></div>
          <div id="rc-faqs"></div>
          ${meta ? `<p class="rc-updated">Last compiled: ${formatDate(meta.compiled_at)}</p>` : ''}
        </div>
      `;
    }

    // Quick filter chips
    const chipsEl = root.querySelector('#rc-chips');
    if (chipsEl) chipsEl.innerHTML = renderChipBar();

    // Featured spotlights (only on unfiltered view)
    const featuredEl = root.querySelector('#rc-featured');
    if (featuredEl) {
      featuredEl.innerHTML = isDefaultView ? renderFeaturedSpotlights() : '';
    }

    const resultsBar = root.querySelector('#rc-results-bar');
    if (resultsBar) resultsBar.innerHTML = renderResultsBar(filtered.length);

    const resultsDiv = root.querySelector('#rc-results');
    if (resultsDiv) {
      if (filtered.length === 0) {
        resultsDiv.innerHTML = renderEmptyState();
      } else {
        let content = '';
        for (const [monthKey, monthReleases] of groups.entries()) {
          content += renderMonthSection(monthKey, monthReleases);
        }
        resultsDiv.innerHTML = content;
      }
    }

    const faqEl = root.querySelector('#rc-faqs');
    if (faqEl) faqEl.innerHTML = renderContextualFaqs(filtered);
  }

  /**
   * Render featured spotlight blocks shown only on the default (unfiltered) view.
   * - Pinned upcoming releases (soonest 3)
   * - Work & Safety spotlight (competitive differentiator)
   */
  function renderFeaturedSpotlights() {
    let html = '';

    // --- Upcoming spotlight ---
    const upcoming = allReleases
      .filter(r => r._is_upcoming && r._days_until !== null && r._days_until >= 0)
      .sort((a, b) => (a._sort_key || '').localeCompare(b._sort_key || ''))
      .slice(0, 3);

    if (upcoming.length > 0) {
      html += `
        <section class="rc-spotlight" aria-label="Coming soon">
          <div class="rc-spotlight__header">
            <h2 class="rc-spotlight__title">Coming Soon</h2>
            <a href="?status=upcoming" class="rc-spotlight__link">See all upcoming</a>
          </div>
          <div class="rc-spotlight__cards">
            ${upcoming.map(r => renderCard(r)).join('')}
          </div>
        </section>
      `;
    }

    // --- Work & Safety spotlight ---
    const workReleases = allReleases
      .filter(r => r.category === 'work_safety')
      .slice(0, 3);

    if (workReleases.length > 0) {
      html += `
        <section class="rc-spotlight rc-spotlight--work" aria-label="Work and safety footwear">
          <div class="rc-spotlight__header">
            <h2 class="rc-spotlight__title">Work &amp; Safety Boots</h2>
            <a href="?category=work_safety" class="rc-spotlight__link">See all work boots</a>
          </div>
          <p class="rc-spotlight__desc">
            Work boot and nursing shoe releases — the releases no other calendar tracks.
          </p>
          <div class="rc-spotlight__cards">
            ${workReleases.map(r => renderCard(r)).join('')}
          </div>
        </section>
      `;
    }

    // --- Nursing & Healthcare spotlight ---
    const nursingReleases = allReleases
      .filter(r => r.category === 'walking_everyday' && r.activity === 'work')
      .slice(0, 2);

    if (nursingReleases.length > 0) {
      html += `
        <section class="rc-spotlight rc-spotlight--nursing" aria-label="Nursing and healthcare footwear">
          <div class="rc-spotlight__header">
            <h2 class="rc-spotlight__title">Nursing &amp; Healthcare</h2>
            <a href="?category=walking_everyday" class="rc-spotlight__link">See all nursing shoes</a>
          </div>
          <div class="rc-spotlight__cards">
            ${nursingReleases.map(r => renderCard(r)).join('')}
          </div>
        </section>
      `;
    }

    return html;
  }

  function renderResultsBar(count) {
    const activeFilters = Object.entries(state).filter(([k, v]) => v && v !== '' && v !== 'date_asc' && k !== 'sort').length;
    return `
      <div class="rc-results-bar">
        <span><span class="rc-results-bar__count">${count}</span> release${count !== 1 ? 's' : ''}${activeFilters > 0 ? ' matching filters' : ''}</span>
        <div class="rc-results-bar__sort">
          <label for="rc-sort" style="font-size:0.8125rem;opacity:.6">Sort:</label>
          <select id="rc-sort" style="font-size:0.8125rem;padding:0.25rem 1.5rem 0.25rem 0.375rem;border:1px solid rgb(var(--color-border,226 232 240));border-radius:4px;background:transparent;appearance:none;background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24'%3E%3Cpath fill='%236b7280' d='M7 10l5 5 5-5H7z'/%3E%3C/svg%3E\");background-repeat:no-repeat;background-position:right 0.25rem center;cursor:pointer">
            <option value="date_asc"  ${state.sort === 'date_asc'   ? 'selected' : ''}>Release Date</option>
            <option value="brand_asc" ${state.sort === 'brand_asc'  ? 'selected' : ''}>Brand A–Z</option>
            <option value="date_desc" ${state.sort === 'date_desc'  ? 'selected' : ''}>Oldest First</option>
          </select>
        </div>
      </div>
    `;
  }

  function renderMonthSection(monthKey, releases) {
    const label = monthKey === '_unknown' ? 'Date Unknown' : formatMonthKey(monthKey);
    const isUpcomingMonth = releases.some(r => r._is_upcoming);
    const sectionClass = 'rc-month' + (isUpcomingMonth ? ' rc-month--upcoming' : '');
    return `
      <section class="${sectionClass}" aria-label="${escHtml(label)}">
        <div class="rc-month__header">
          <h2 class="rc-month__title">${escHtml(label)}</h2>
          <span class="rc-month__count">${releases.length} release${releases.length !== 1 ? 's' : ''}</span>
        </div>
        <div class="rc-month__releases">
          ${releases.map(r => renderCard(r)).join('')}
        </div>
      </section>
    `;
  }

  // -------------------------------------------------------------------------
  // Category placeholder SVG
  // -------------------------------------------------------------------------

  function renderCategoryPlaceholder(category, subcategory) {
    const svgKey = SVG_SHOES[category] ? category
      : (subcategory && SVG_SHOES[subcategory]) ? subcategory
      : 'default';
    const svg = SVG_SHOES[svgKey] || SVG_SHOES['default'];
    const tint = PLACEHOLDER_TINTS[category] || PLACEHOLDER_TINTS['default'];
    return `<div class="rc-card__placeholder" style="background:${tint}" aria-hidden="true">${svg}</div>`;
  }

  // -------------------------------------------------------------------------
  // Verdict stars
  // -------------------------------------------------------------------------

  function renderVerdict(stars, label) {
    if (!stars || !label) return '';
    const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const starHtml = [1,2,3,4,5].map(function(i) {
      return `<span class="rc-verdict__star${i <= stars ? ' rc-verdict__star--filled' : ''}" aria-hidden="true">★</span>`;
    }).join('');
    return `<div class="rc-verdict rc-verdict--${escHtml(slug)}"><span class="rc-verdict__stars" aria-label="${stars} of 5 stars">${starHtml}</span><span class="rc-verdict__label">${escHtml(label)}</span></div>`;
  }

  // -------------------------------------------------------------------------
  // What Changed — visual bullets
  // -------------------------------------------------------------------------

  function renderWhatChangedBullets(whatChanged) {
    if (!whatChanged) return '';

    const FIELDS = ['midsole','upper','outsole','fit','weight','drop','stack','safety','other'];
    const bullets = [];

    for (var i = 0; i < FIELDS.length && bullets.length < 4; i++) {
      var val = whatChanged[FIELDS[i]];
      if (!val) continue;
      // First sentence, max 72 chars
      var text = val.split(/[.!?]/)[0].trim();
      if (text.length > 72) text = text.slice(0, 70) + '…';
      bullets.push(`<li class="rc-changes__item"><span class="rc-changes__check" aria-hidden="true">✓</span><span>${escHtml(text)}</span></li>`);
    }

    // Fall back to summary if no specific field bullets
    if (bullets.length === 0 && whatChanged.summary) {
      var s = whatChanged.summary.split(/[.!?]/)[0].trim();
      if (s.length > 80) s = s.slice(0, 78) + '…';
      if (s) bullets.push(`<li class="rc-changes__item"><span class="rc-changes__check" aria-hidden="true">✓</span><span>${escHtml(s)}</span></li>`);
    }

    if (bullets.length === 0) return '';

    return `<div class="rc-card__section rc-card__section--changes">
      <span class="rc-card__section-label">What changed</span>
      <ul class="rc-changes">${bullets.join('')}</ul>
    </div>`;
  }

  // -------------------------------------------------------------------------
  // Who Should Buy / Skip — audience section
  // -------------------------------------------------------------------------

  function renderAudience(whoShouldBuy, whoShouldSkip) {
    if (!whoShouldBuy && !whoShouldSkip) return '';

    var buyHtml = whoShouldBuy
      ? `<div class="rc-audience__col">
           <span class="rc-audience__label rc-audience__label--buy">Good for</span>
           <p class="rc-audience__text">${escHtml(whoShouldBuy)}</p>
         </div>` : '';

    var skipHtml = whoShouldSkip
      ? `<div class="rc-audience__col">
           <span class="rc-audience__label rc-audience__label--skip">Not for</span>
           <p class="rc-audience__text">${escHtml(whoShouldSkip)}</p>
         </div>` : '';

    return `<div class="rc-card__section rc-card__section--audience">
      <div class="rc-audience">${buyHtml}${skipHtml}</div>
    </div>`;
  }

  function renderQuickAnswer(answer) {
    if (!answer) return '';

    return `<p class="rc-card__quick-answer">
      <span class="rc-card__quick-answer-label">Quick answer</span>
      <span>${escHtml(answer)}</span>
    </p>`;
  }

  function renderIntelDetails(answers, faqs) {
    if (!answers && (!faqs || faqs.length === 0)) return '';

    var rows = [];
    if (answers && answers.bottom_line) {
      rows.push(renderIntelRow('Bottom line', answers.bottom_line));
    }
    if (answers && answers.biggest_improvement) {
      rows.push(renderIntelRow('Biggest improvement', answers.biggest_improvement));
    }
    if (answers && answers.confidence_explanation) {
      rows.push(renderIntelRow('Confidence', answers.confidence_explanation));
    }

    var faqHtml = '';
    if (faqs && faqs.length > 0) {
      faqHtml = `<div class="rc-card__intel-faqs">
        <span class="rc-card__intel-label">Common questions</span>
        ${faqs.slice(0, 3).map(function(faq) {
          return `<details class="rc-card__intel-faq">
            <summary>${escHtml(faq.question)}</summary>
            <p>${escHtml(faq.answer)}</p>
          </details>`;
        }).join('')}
      </div>`;
    }

    if (rows.length === 0 && !faqHtml) return '';

    return `<details class="rc-card__intel">
      <summary class="rc-card__intel-summary">More answers</summary>
      <div class="rc-card__intel-body">
        ${rows.length > 0 ? `<dl class="rc-card__intel-grid">${rows.join('')}</dl>` : ''}
        ${faqHtml}
      </div>
    </details>`;
  }

  function renderIntelRow(label, value) {
    return `<div class="rc-card__intel-row">
      <dt>${escHtml(label)}</dt>
      <dd>${escHtml(value)}</dd>
    </div>`;
  }

  function getCategoryFaqGroup(category) {
    if (!category || !faqGroups || !Array.isArray(faqGroups.categories)) return null;

    for (var i = 0; i < faqGroups.categories.length; i++) {
      var group = faqGroups.categories[i];
      if (group && group.meta && group.meta.category === category) return group;
      if (group && group.id === category) return group;
    }

    return null;
  }

  function getContextualFaqState(filtered) {
    var categoryGroup = getCategoryFaqGroup(state.category);
    var siteGroups = Array.isArray(faqGroups.site) ? faqGroups.site : [];
    var siteFaqs = [];
    for (var i = 0; i < siteGroups.length; i++) {
      if (siteGroups[i] && Array.isArray(siteGroups[i].faqs)) {
        siteFaqs = siteFaqs.concat(siteGroups[i].faqs);
      }
    }

    if (state.category && categoryGroup) {
      return {
        title: (CATEGORY_LABELS[state.category] || 'This category') + ' questions',
        intro: `Useful questions for shoppers researching ${CATEGORY_LABELS[state.category] || 'this category'}.`,
        faqs: categoryGroup.faqs.concat(siteFaqs.slice(0, 2)),
      };
    }

    if (state.brand && filtered.length > 0) {
      var brandName = filtered[0].brand;
      return {
        title: brandName + ' release questions',
        intro: `Answers pulled from the releases currently visible for ${brandName}.`,
        faqs: collectReleaseFaqs(filtered, 6),
      };
    }

    if (filtered.length === 1) {
      return {
        title: 'Release questions',
        intro: 'The key questions buyers ask about this release.',
        faqs: collectReleaseFaqs(filtered, 6),
      };
    }

    return {
      title: 'Footwear release questions',
      intro: 'Helpful answers about release timing, confidence, and whether it makes sense to wait.',
      faqs: siteFaqs.slice(0, 8),
    };
  }

  function collectReleaseFaqs(releases, limit) {
    var seen = new Set();
    var collected = [];

    for (var i = 0; i < releases.length; i++) {
      var faqs = releases[i]._faq || [];
      for (var j = 0; j < faqs.length; j++) {
        var faq = faqs[j];
        var key = faq.question + '|' + faq.answer;
        if (seen.has(key)) continue;
        seen.add(key);
        collected.push(faq);
        if (collected.length >= limit) return collected;
      }
    }

    return collected;
  }

  function renderContextualFaqs(filtered) {
    var contextual = getContextualFaqState(filtered);
    if (!contextual || !contextual.faqs || contextual.faqs.length === 0) return '';

    return `<section class="rc-faq-panel" aria-labelledby="rc-faq-panel-title">
      <div class="rc-faq-panel__header">
        <h2 class="rc-faq-panel__title" id="rc-faq-panel-title">${escHtml(contextual.title)}</h2>
        <p class="rc-faq-panel__intro">${escHtml(contextual.intro)}</p>
      </div>
      <div class="rc-faq-panel__list">
        ${contextual.faqs.map(function(faq) {
          return `<details class="rc-faq-panel__item">
            <summary>${escHtml(faq.question)}</summary>
            <div class="rc-faq-panel__answer"><p>${escHtml(faq.answer)}</p></div>
          </details>`;
        }).join('')}
      </div>
    </section>`;
  }

  function getConfidenceLabel(confidence) {
    var labels = {
      confirmed: 'Confirmed',
      retailer: 'Confirmed',
      trade_show: 'Likely',
      brand_comm: 'Likely',
      historical: 'Estimated',
      community: 'Estimated',
    };
    return labels[confidence] || confidence || 'Unknown';
  }

  function getCalendarMeta(release) {
    if (!release || !release.release_date) return null;

    var parts = release.release_date.split('-');
    if (parts.length === 3) {
      return {
        precision: 'day',
        label: 'Add release to calendar',
        title: release.brand + ' ' + release.model + ' release',
        start: new Date(Date.UTC(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10))),
        exactDate: release.release_date,
      };
    }

    if (parts.length === 2) {
      return {
        precision: 'month',
        label: 'Add estimated release window',
        title: release.brand + ' ' + release.model + ' estimated release window',
        start: new Date(Date.UTC(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, 1)),
        exactDate: null,
      };
    }

    return null;
  }

  function buildCalendarDescription(release, meta) {
    var lines = [];
    if (meta.precision === 'month') {
      lines.push('Estimated release window: ' + (release._date_label || release.release_date) + '.');
      lines.push('This is not an exact release date.');
    } else {
      lines.push('Release date: ' + (release._date_label || release.release_date) + '.');
    }

    if (release._answers && release._answers.quick_answer) {
      lines.push(release._answers.quick_answer);
    } else if (release._quick_answer) {
      lines.push(release._quick_answer);
    }

    lines.push('Confidence: ' + getConfidenceLabel(release.release_confidence) + '.');
    lines.push('Sundial release page: ' + buildAbsoluteReleaseUrl(release));

    return lines.join('\n');
  }

  function buildAbsoluteReleaseUrl(release) {
    var relative = release._release_url || ('/pages/shoe-release-calendar#' + release.id);
    return window.location.origin + relative;
  }

  function addDays(date, days) {
    return new Date(date.getTime() + (days * 86400000));
  }

  function toIcsDate(date) {
    var year = date.getUTCFullYear();
    var month = String(date.getUTCMonth() + 1).padStart(2, '0');
    var day = String(date.getUTCDate()).padStart(2, '0');
    return '' + year + month + day;
  }

  function toIcsTimestamp(date) {
    var year = date.getUTCFullYear();
    var month = String(date.getUTCMonth() + 1).padStart(2, '0');
    var day = String(date.getUTCDate()).padStart(2, '0');
    var hours = String(date.getUTCHours()).padStart(2, '0');
    var minutes = String(date.getUTCMinutes()).padStart(2, '0');
    var seconds = String(date.getUTCSeconds()).padStart(2, '0');
    return '' + year + month + day + 'T' + hours + minutes + seconds + 'Z';
  }

  function escapeIcs(value) {
    return String(value || '')
      .replace(/\\/g, '\\\\')
      .replace(/\n/g, '\\n')
      .replace(/,/g, '\\,')
      .replace(/;/g, '\\;');
  }

  function buildIcsContent(release, meta) {
    var start = meta.start;
    var end = addDays(start, 1);
    var timestamp = toIcsTimestamp(new Date());
    var uid = release.id + '@sundial777.com';
    var description = buildCalendarDescription(release, meta);
    var url = buildAbsoluteReleaseUrl(release);

    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Sundial//Footwear Intel//EN',
      'CALSCALE:GREGORIAN',
      'BEGIN:VEVENT',
      'UID:' + uid,
      'DTSTAMP:' + timestamp,
      'DTSTART;VALUE=DATE:' + toIcsDate(start),
      'DTEND;VALUE=DATE:' + toIcsDate(end),
      'SUMMARY:' + escapeIcs(meta.title),
      'DESCRIPTION:' + escapeIcs(description),
      'URL:' + escapeIcs(url),
      'BEGIN:VALARM',
      'ACTION:DISPLAY',
      'DESCRIPTION:' + escapeIcs(meta.precision === 'month' ? 'Estimated release window reminder' : 'Release reminder'),
      'TRIGGER:-P1D',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
  }

  function downloadCalendarEvent(releaseId) {
    var release = allReleases.find(function(item) { return item.id === releaseId; });
    if (!release) return;

    var meta = getCalendarMeta(release);
    if (!meta) return;

    var blob = new Blob([buildIcsContent(release, meta)], { type: 'text/calendar;charset=utf-8' });
    var blobUrl = URL.createObjectURL(blob);
    var link = document.createElement('a');
    link.href = blobUrl;
    link.download = release.id + (meta.precision === 'month' ? '-estimated-release-window.ics' : '-release.ics');
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(function() {
      URL.revokeObjectURL(blobUrl);
    }, 1000);
  }

  function buildGoogleCalendarUrl(release, meta) {
    if (!meta || meta.precision !== 'day') return null;

    var start = toIcsDate(meta.start);
    var end = toIcsDate(addDays(meta.start, 1));
    var params = new URLSearchParams({
      action: 'TEMPLATE',
      text: meta.title,
      dates: start + '/' + end,
      details: buildCalendarDescription(release, meta),
      location: buildAbsoluteReleaseUrl(release),
    });

    return 'https://calendar.google.com/calendar/render?' + params.toString();
  }

  function renderCalendarActionButtons(release) {
    var meta = getCalendarMeta(release);
    if (!meta) return [];

    var googleUrl = buildGoogleCalendarUrl(release, meta);
    var actions = [
      `<button class="rc-card__action rc-card__action--calendar" type="button" data-calendar-id="${escHtml(release.id)}">${escHtml(meta.label)}</button>`,
    ];

    if (googleUrl) {
      actions.push(`<a class="rc-card__action rc-card__action--calendar-link" href="${escHtml(googleUrl)}" target="_blank" rel="noopener noreferrer">Google Calendar</a>`);
    }

    return actions;
  }

  function renderReleaseActions(release) {
    var actions = renderCalendarActionButtons(release);
    var alertText = release._alert_action_text || 'Notify me';
    var shareLabel = 'Share';
    var contextAttrs = [
      `data-rc-alert-open="release"`,
      `data-alert-release-id="${escHtml(release.id)}"`,
      `data-alert-brand-slug="${escHtml(release.brand_slug || '')}"`,
      `data-alert-category="${escHtml(release.category || '')}"`,
    ].join(' ');

    actions.push(`<button class="rc-card__action rc-card__action--notify" type="button" ${contextAttrs}>${escHtml(alertText)}</button>`);
    actions.push(`<button class="rc-card__action rc-card__action--share" type="button" data-share-id="${escHtml(release.id)}">${shareLabel}</button>`);

    return `<div class="rc-card__actions" role="group" aria-label="Release actions">${actions.join('')}</div>`;
  }

  function populateAlertUi() {
    if (!alertsConfig) return;

    var brandPreview = document.querySelector('[data-rc-alert-preview-brands]');
    if (brandPreview) {
      brandPreview.innerHTML = (alertsConfig.brands || [])
        .filter(function(item) { return item.count > 0; })
        .slice(0, 4)
        .map(function(item) {
          return `<span class="rc-alert-pill">${escHtml(item.label)}</span>`;
        })
        .join('');
    }

    var categoryPreview = document.querySelector('[data-rc-alert-preview-categories]');
    if (categoryPreview) {
      categoryPreview.innerHTML = (alertsConfig.categories || [])
        .filter(function(item) { return item.count > 0 || !item.value; })
        .slice(0, 4)
        .map(function(item) {
          return `<span class="rc-alert-pill rc-alert-pill--soft">${escHtml(item.label)}</span>`;
        })
        .join('');
    }

    var brandsContainer = document.querySelector('[data-rc-alert-brands]');
    if (brandsContainer) {
      brandsContainer.innerHTML = (alertsConfig.brands || []).map(function(item) {
        return `<label class="rc-alert-option">
          <input type="checkbox" value="${escHtml(item.slug)}" data-alert-brand>
          <span>${escHtml(item.label)}</span>
        </label>`;
      }).join('');
    }

    var categoriesContainer = document.querySelector('[data-rc-alert-categories]');
    if (categoriesContainer) {
      categoriesContainer.innerHTML = (alertsConfig.categories || []).map(function(item) {
        return `<label class="rc-alert-option">
          <input type="checkbox" value="${escHtml(item.alert_key)}" data-alert-category>
          <span>${escHtml(item.label)}</span>
        </label>`;
      }).join('');
    }
  }

  function attachAlertModal() {
    if (alertModalInitialized) return;
    alertModalInitialized = true;

    document.addEventListener('click', function(event) {
      var openTarget = event.target.closest('[data-rc-alert-open]');
      if (openTarget) {
        openAlertModal(openTarget);
        return;
      }

      if (event.target.closest('[data-rc-alert-close]')) {
        closeAlertModal();
        return;
      }

      var shareTarget = event.target.closest('[data-share-id]');
      if (shareTarget) {
        shareRelease(shareTarget.getAttribute('data-share-id'), shareTarget);
      }
    });

    document.addEventListener('keydown', function(event) {
      if (event.key === 'Escape') closeAlertModal();
    });

    var form = document.getElementById('RcAlertForm');
    if (form) {
      form.addEventListener('submit', prepareAlertFormPayload);
    }

    var modal = document.getElementById('rc-alert-modal');
    if (modal && modal.querySelector('.rc-alert-form__message')) {
      openAlertModal();
    }
  }

  function openAlertModal(trigger) {
    var modal = document.getElementById('rc-alert-modal');
    if (!modal) return;

    activeAlertContext = buildAlertContext(trigger);
    modal.hidden = false;
    document.documentElement.classList.add('rc-alert-modal-open');
    syncAlertSelections();
    syncAlertContextUi();

    var emailInput = modal.querySelector('#RcAlertEmail');
    if (emailInput && !emailInput.value) {
      emailInput.focus();
    }
  }

  function closeAlertModal() {
    var modal = document.getElementById('rc-alert-modal');
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.documentElement.classList.remove('rc-alert-modal-open');
  }

  function buildAlertContext(trigger) {
    var base = { source: 'release-calendar', releaseId: null, brandSlug: null, category: null };
    if (!trigger) return base;

    return {
      source: trigger.getAttribute('data-rc-alert-open') || 'release-calendar',
      releaseId: trigger.getAttribute('data-alert-release-id'),
      brandSlug: trigger.getAttribute('data-alert-brand-slug'),
      category: trigger.getAttribute('data-alert-category'),
    };
  }

  function syncAlertSelections() {
    var modal = document.getElementById('rc-alert-modal');
    if (!modal) return;

    modal.querySelectorAll('[data-alert-brand]').forEach(function(input) {
      input.checked = !!(activeAlertContext && activeAlertContext.brandSlug && input.value === activeAlertContext.brandSlug);
    });

    modal.querySelectorAll('[data-alert-category]').forEach(function(input) {
      input.checked = !!(activeAlertContext && activeAlertContext.category && mapCategoryToAlertKey(activeAlertContext.category) === input.value);
    });
  }

  function syncAlertContextUi() {
    var modal = document.getElementById('rc-alert-modal');
    if (!modal) return;

    var contextEl = modal.querySelector('[data-rc-alert-context]');
    if (!contextEl) return;

    var chips = [];
    var release = getReleaseById(activeAlertContext && activeAlertContext.releaseId);
    if (release) {
      chips.push(`<span class="rc-alert-pill">${escHtml(release.brand + ' ' + release.model + ' ' + release.version_label)}</span>`);
    }

    if (!release && activeAlertContext && activeAlertContext.brandSlug) {
      var brand = findAlertBrand(activeAlertContext.brandSlug);
      if (brand) chips.push(`<span class="rc-alert-pill">${escHtml(brand.label)}</span>`);
    }

    if (activeAlertContext && activeAlertContext.category) {
      var category = findAlertCategory(mapCategoryToAlertKey(activeAlertContext.category));
      if (category) chips.push(`<span class="rc-alert-pill rc-alert-pill--soft">${escHtml(category.label)}</span>`);
    }

    contextEl.innerHTML = chips.join('');
    contextEl.hidden = chips.length === 0;
  }

  function prepareAlertFormPayload() {
    var form = document.getElementById('RcAlertForm');
    if (!form) return;

    var selectedBrands = Array.prototype.slice.call(form.querySelectorAll('[data-alert-brand]:checked')).map(function(input) { return input.value; });
    var selectedCategories = Array.prototype.slice.call(form.querySelectorAll('[data-alert-category]:checked')).map(function(input) { return input.value; });
    var release = getReleaseById(activeAlertContext && activeAlertContext.releaseId);
    var interestsInput = form.querySelector('[data-rc-alert-interests]');
    var noteField = form.querySelector('[data-rc-alert-note]');
    var tagsField = form.querySelector('[data-rc-alert-tags]');
    var interests = interestsInput ? interestsInput.value.trim() : '';

    var tags = ['footwear-intel-alerts', 'source-release-calendar'];
    selectedBrands.forEach(function(brand) { tags.push('alert-brand-' + slugifyToken(brand)); });
    selectedCategories.forEach(function(category) { tags.push('alert-category-' + slugifyToken(category)); });

    if (release) {
      tags.push('alert-release-' + slugifyToken(release.id));
      tags.push('alert-notification-release-confirmed');
      tags.push('alert-notification-release-delayed');
      tags.push('alert-notification-available-at-sundial');
      tags.push('alert-notification-major-review-published');
    }

    if (interests) {
      interests.split(/[\n,]+/).map(function(item) { return item.trim(); }).filter(Boolean).slice(0, 5).forEach(function(item) {
        tags.push('alert-interest-' + slugifyToken(item));
      });
    }

    if (tagsField) tagsField.value = dedupe(tags).join(', ');

    if (noteField) {
      noteField.value = JSON.stringify({
        source: 'release-calendar',
        release_id: release ? release.id : null,
        selected_brands: selectedBrands,
        selected_categories: selectedCategories,
        interests: interests || null,
        provider_targets: alertsConfig && alertsConfig.provider_targets ? alertsConfig.provider_targets : [],
      });
    }
  }

  function shareRelease(releaseId, button) {
    var release = getReleaseById(releaseId);
    if (!release) return;

    var url = buildAbsoluteReleaseUrl(release);
    var title = release.brand + ' ' + release.model + ' ' + release.version_label;
    if (navigator.share) {
      navigator.share({ title: title, text: release._quick_answer || release._date_label || title, url: url }).catch(function() {});
      return;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(function() {
        if (!button) return;
        var original = button.textContent;
        button.textContent = 'Link copied';
        setTimeout(function() {
          button.textContent = original;
        }, 1400);
      }).catch(function() {});
    }
  }

  function scrollToHashedRelease() {
    var hash = window.location.hash;
    if (!hash) return;
    var id = hash.slice(1);
    setTimeout(function() {
      var el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('rc-card--highlighted');
      setTimeout(function() { el.classList.remove('rc-card--highlighted'); }, 2500);
    }, 200);
  }

  function getReleaseById(releaseId) {
    if (!releaseId) return null;
    for (var i = 0; i < allReleases.length; i++) {
      if (allReleases[i].id === releaseId) return allReleases[i];
    }
    return null;
  }

  function findAlertBrand(slug) {
    if (!alertsConfig || !alertsConfig.brands) return null;
    for (var i = 0; i < alertsConfig.brands.length; i++) {
      if (alertsConfig.brands[i].slug === slug) return alertsConfig.brands[i];
    }
    return null;
  }

  function findAlertCategory(alertKey) {
    if (!alertsConfig || !alertsConfig.categories) return null;
    for (var i = 0; i < alertsConfig.categories.length; i++) {
      if (alertsConfig.categories[i].alert_key === alertKey) return alertsConfig.categories[i];
    }
    return null;
  }

  function mapCategoryToAlertKey(category) {
    var matches = {
      road_running: 'running',
      trail_running: 'trail',
      work_safety: 'work_boots',
      walking_everyday: 'walking',
      sandals_slides: 'recovery',
    };
    return matches[category] || category;
  }

  function slugifyToken(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
  }

  function dedupe(items) {
    return Array.from(new Set(items.filter(Boolean)));
  }

  // -------------------------------------------------------------------------
  // Quick chip bar
  // -------------------------------------------------------------------------

  function renderChipBar() {
    var chips = [
      { label: 'Upcoming',        key: 'status',   value: 'upcoming' },
      { label: 'Running',         key: 'category', value: 'road_running' },
      { label: 'Trail',           key: 'category', value: 'trail_running' },
      { label: 'Work Boots',      key: 'category', value: 'work_safety' },
      { label: 'Nursing',         key: 'category', value: 'walking_everyday' },
      { label: 'Worth Waiting',   key: 'status',   value: 'worth_waiting' },
    ];

    return chips.map(function(chip) {
      var isActive = state[chip.key] === chip.value;
      return `<button class="rc-chip${isActive ? ' rc-chip--active' : ''}" data-chip-key="${escHtml(chip.key)}" data-chip-value="${escHtml(chip.value)}" type="button">${escHtml(chip.label)}</button>`;
    }).join('');
  }

  // -------------------------------------------------------------------------
  // Release card (redesigned)
  // -------------------------------------------------------------------------

  function renderCard(r) {
    // Visual: real image or premium SVG placeholder
    const visualHtml = r.image_url
      ? `<img class="rc-card__image" src="${escHtml(r.image_url)}" alt="${escHtml(r.brand)} ${escHtml(r.model)} ${escHtml(r.version_label)}" loading="lazy" width="96" height="96">`
      : renderCategoryPlaceholder(r.category, r.subcategory);

    // Countdown for upcoming — urgent styling when ≤ 14 days out
    let countdownHtml = '';
    if (r._is_upcoming && r._days_until !== null && r._days_until >= 0) {
      const isUrgent = r._days_until <= 14;
      const label = formatCountdown(r._days_until);
      countdownHtml = isUrgent
        ? `<span class="rc-card__countdown rc-card__countdown--urgent">${escHtml(label)}</span>`
        : `<span class="rc-card__countdown">${escHtml(label)}</span>`;
    }

    // Date + price row (shown immediately after title for scannability)
    const price    = r.msrp ? `<span class="rc-card__price rc-card__price--msrp">$${r.msrp}</span>` : '';
    const prevPrice = (r.predecessor_current_msrp && r._is_upcoming)
      ? `<span class="rc-card__price rc-card__price--prev">Prev $${r.predecessor_current_msrp}</span>` : '';

    // Verdict stars
    const verdictHtml = renderVerdict(r._verdict_stars, r._verdict_label);

    // Buy pill (verdict-buy row only shows the pill; detail text is shown separately below)
    const buyKey   = (r.buy_recommendation && r.buy_recommendation !== 'not_yet_determined') ? r.buy_recommendation : '';
    const buyBadge = buyKey
      ? `<span class="rc-buy rc-buy--${escHtml(buyKey)}">${escHtml(BUY_LABELS[buyKey] || buyKey)}</span>` : '';

    // Verdict + buy pill combined on one line — the one-glance recommendation
    const verdictBuyHtml = (verdictHtml || buyBadge)
      ? `<div class="rc-card__verdict-buy-row">${verdictHtml}${buyBadge}</div>` : '';

    // Quick answer
    const quickAnswerHtml = renderQuickAnswer(r._answers && r._answers.quick_answer ? r._answers.quick_answer : r._quick_answer);

    // What changed bullets
    const changesHtml = renderWhatChangedBullets(r.what_changed);

    // Who Should Buy / Skip
    const audienceHtml = renderAudience(r.who_should_buy, r.who_should_skip);

    // Buy detail text — the full "why" paragraph, shown after audience
    const buyDetail = r.buy_recommendation_detail || '';
    const buyDetailHtml = (buyKey || buyDetail)
      ? `<div class="rc-card__section rc-card__section--buy">
           <span class="rc-card__section-label">Buy now or wait?</span>
           <p class="rc-card__section-text">${escHtml(buyDetail)}</p>
         </div>` : '';

    const intelHtml = renderIntelDetails(r._answers, r._faq);
    const actionsHtml = renderReleaseActions(r);

    // Footer: verified + CTA
    const verifiedDate = r.last_verified || r.last_updated;
    const verifiedHtml = verifiedDate ? `<span class="rc-card__verified">Verified ${formatDate(verifiedDate)}</span>` : '';
    const ctaHtml = r._cta_url
      ? `<a href="${escHtml(r._cta_url)}" class="rc-card__cta">${escHtml(r._cta_text)}</a>`
      : '';

    // Sources — collapsed
    let sourcesHtml = '';
    if (r.source_urls && r.source_urls.length > 0) {
      const links = r.source_urls.map(function(u) {
        let label;
        try { label = new URL(u).hostname.replace(/^www\./, ''); } catch(_) { label = u; }
        return `<a href="${escHtml(u)}" class="rc-card__source-link" target="_blank" rel="nofollow noopener noreferrer">${escHtml(label)} ↗</a>`;
      }).join('');
      sourcesHtml = `<details class="rc-card__sources"><summary class="rc-card__sources-toggle">Release sources</summary><div class="rc-card__sources-list">${links}</div></details>`;
    }

    const cardClass = 'rc-card'
      + (r._is_upcoming ? ' rc-card--upcoming' : '')
      + (r._is_released ? ' rc-card--released' : '');

    return `
      <article class="${cardClass}" id="${escHtml(r.id)}">
        <div class="rc-card__visual">${visualHtml}</div>
        <div class="rc-card__body">
          <div class="rc-card__meta">
            <span class="rc-card__brand">${escHtml(r.brand)}</span>
            ${renderStatusBadge(r.release_status)}
            ${countdownHtml}
          </div>
          <h3 class="rc-card__title">${escHtml(r.model)} ${escHtml(r.version_label)}</h3>
          <div class="rc-card__date">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <span>${escHtml(r._date_label || 'Date unknown')}</span>
            ${price}
            ${prevPrice}
            ${renderConfidenceIndicator(r.release_confidence)}
          </div>
          ${verdictBuyHtml}
          ${quickAnswerHtml}
          ${changesHtml}
          ${audienceHtml}
          ${buyDetailHtml}
          ${intelHtml}
          ${actionsHtml}
          <div class="rc-card__footer">
            ${verifiedHtml}
            ${ctaHtml}
          </div>
          ${sourcesHtml}
        </div>
      </article>
    `;
  }

  function renderStatusBadge(status) {
    const label = RELEASE_STATUS_LABELS[status] || status;
    return `<span class="rc-badge rc-badge--${escHtml(status)}"><span class="rc-badge__dot" aria-hidden="true"></span>${escHtml(label)}</span>`;
  }

  function renderConfidenceIndicator(confidence) {
    if (!confidence) return '';
    const levels = { confirmed: 3, retailer: 3, trade_show: 2, brand_comm: 2, historical: 1, community: 1 };
    const level = levels[confidence] || 0;
    const label = getConfidenceLabel(confidence);
    const bars = [1, 2, 3].map(i =>
      `<span class="rc-confidence__bar${i <= level ? ' rc-confidence__bar--active' : ''}"></span>`
    ).join('');
    return `<span class="rc-confidence rc-confidence--${escHtml(confidence)}" title="Date confidence: ${escHtml(label)}"><span class="rc-confidence__bars" aria-hidden="true">${bars}</span><span class="rc-confidence__label">${escHtml(label)}</span></span>`;
  }

  function renderEmptyState() {
    return `
      <div class="rc-empty" role="status">
        <svg class="rc-empty__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
        <h3 class="rc-empty__title">No releases match your filters</h3>
        <p class="rc-empty__body">Try adjusting your filters or search terms.</p>
        <button class="rc-empty__reset" id="rc-empty-reset">Clear all filters</button>
      </div>
    `;
  }

  // -------------------------------------------------------------------------
  // Filter UI population
  // -------------------------------------------------------------------------

  function populateFilters(root) {
    const controlsEl = root.querySelector('#rc-controls');
    if (!controlsEl) return;

    // Extract unique values
    const brands = [...new Map(allReleases.map(r => [r.brand_slug, r.brand])).entries()]
      .sort((a, b) => a[1].localeCompare(b[1]));
    const categories = [...new Set(allReleases.map(r => r.category).filter(Boolean))];
    const genders = [...new Set(allReleases.map(r => r.gender).filter(Boolean))];

    controlsEl.innerHTML = `
      <div class="rc-search">
        <svg class="rc-search__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
        <input class="rc-search__input" type="text" id="rc-search" placeholder="Search brands, models…" value="${escHtml(state.search || '')}" aria-label="Search releases">
      </div>
      <div class="rc-filter-group">
        <span class="rc-filter-group__label">Brand</span>
        <select id="rc-filter-brand" aria-label="Filter by brand">
          <option value="">All Brands</option>
          ${brands.map(([slug, label]) => `<option value="${escHtml(slug)}" ${state.brand === slug ? 'selected' : ''}>${escHtml(label)}</option>`).join('')}
        </select>
      </div>
      <div class="rc-filter-group">
        <span class="rc-filter-group__label">Category</span>
        <select id="rc-filter-category" aria-label="Filter by category">
          <option value="">All Categories</option>
          ${categories.map(c => `<option value="${escHtml(c)}" ${state.category === c ? 'selected' : ''}>${escHtml(CATEGORY_LABELS[c] || c)}</option>`).join('')}
        </select>
      </div>
      <div class="rc-filter-group">
        <span class="rc-filter-group__label">Status</span>
        <select id="rc-filter-status" aria-label="Filter by status">
          <option value="">All Statuses</option>
          <option value="upcoming" ${state.status === 'upcoming' ? 'selected' : ''}>Upcoming</option>
          <option value="soon"     ${state.status === 'soon'     ? 'selected' : ''}>Coming Soon (60 days)</option>
          <option value="released" ${state.status === 'released' ? 'selected' : ''}>Released</option>
          <option value="recent"   ${state.status === 'recent'   ? 'selected' : ''}>Recently Released</option>
          <option value="rumored"  ${state.status === 'rumored'  ? 'selected' : ''}>Rumored</option>
        </select>
      </div>
      <button class="rc-filters__reset" id="rc-reset" aria-label="Clear all filters">Clear filters</button>
    `;
  }

  function syncFilterUI(root) {
    const search = root.querySelector('#rc-search');
    const brand  = root.querySelector('#rc-filter-brand');
    const cat    = root.querySelector('#rc-filter-category');
    const status = root.querySelector('#rc-filter-status');
    const sort   = root.querySelector('#rc-sort');
    if (search) search.value = state.search || '';
    if (brand)  brand.value  = state.brand  || '';
    if (cat)    cat.value    = state.category || '';
    if (status) status.value = state.status || '';
    if (sort)   sort.value   = state.sort || 'date_asc';
  }

  // -------------------------------------------------------------------------
  // Event listeners
  // -------------------------------------------------------------------------

  let searchDebounce = null;

  function attachListeners(root) {
    root.addEventListener('change', function (e) {
      const id = e.target.id;
      if (id === 'rc-filter-brand')    { state.brand    = e.target.value || null; refresh(root); }
      if (id === 'rc-filter-category') { state.category = e.target.value || null; refresh(root); }
      if (id === 'rc-filter-status')   { state.status   = e.target.value || null; refresh(root); }
      if (id === 'rc-sort')            { state.sort     = e.target.value; refresh(root); }
    });

    root.addEventListener('input', function (e) {
      if (e.target.id !== 'rc-search') return;
      clearTimeout(searchDebounce);
      searchDebounce = setTimeout(function () {
        state.search = e.target.value || '';
        refresh(root);
      }, 250);
    });

    root.addEventListener('click', function (e) {
      if (e.target.id === 'rc-reset' || e.target.id === 'rc-empty-reset') {
        resetFilters(root);
        return;
      }
      if (e.target.dataset && e.target.dataset.calendarId) {
        downloadCalendarEvent(e.target.dataset.calendarId);
        return;
      }
      // Quick chip toggle
      if (e.target.classList.contains('rc-chip')) {
        var key   = e.target.dataset.chipKey;
        var value = e.target.dataset.chipValue;
        if (!key || !value) return;
        // Toggle off if already active; otherwise set
        if (state[key] === value) {
          state[key] = null;
        } else {
          // Chips that set status clear other status; chips that set category clear other category
          if (key === 'status')   { state.status   = value; }
          if (key === 'category') { state.category = value; }
        }
        syncFilterUI(root);
        refresh(root);
      }
    });
  }

  function resetFilters(root) {
    state.brand = null;
    state.category = null;
    state.activity = null;
    state.gender = null;
    state.status = null;
    state.year = null;
    state.month = null;
    state.search = '';
    state.sort = 'date_asc';
    syncFilterUI(root);
    refresh(root);
  }

  function refresh(root) {
    render(root);
    pushUrlState();
    // Re-attach sort listener (results bar is re-rendered)
    const sort = root.querySelector('#rc-sort');
    if (sort) {
      sort.removeEventListener('change', onSortChange);
      sort.addEventListener('change', onSortChange);
    }
  }

  function onSortChange(e) {
    state.sort = e.target.value;
    const root = document.getElementById('rc-root');
    if (root) { render(root); pushUrlState(); }
  }

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  function escHtml(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMonthKey(key) {
    if (!key || key === '_unknown') return 'Date Unknown';
    try {
      const [y, m] = key.split('-');
      const d = new Date(Date.UTC(parseInt(y), parseInt(m) - 1, 1));
      return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
    } catch (_) { return key; }
  }

  function formatDate(isoStr) {
    if (!isoStr) return '';
    try {
      return new Date(isoStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (_) { return isoStr; }
  }

  function formatCountdown(days) {
    if (days === null || days === undefined) return '';
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days <= 7)  return `${days} days`;
    if (days <= 30) return `${Math.ceil(days / 7)} weeks`;
    if (days <= 365) return `${Math.ceil(days / 30)} months`;
    return `${Math.ceil(days / 365)} years`;
  }

})();
